import {
  Box,
  Stack,
  Typography,
  Divider,
  styled,
  Chip,
  CircularProgress,
  Grid,
} from "@mui/material";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { formatCurrency } from "@/utils/format-currency";
import { DataTable } from "@/components/custom/data-table";
import { CustomButton } from "@/components/custom/custom-button";
import CustomPagination from "@/components/custom/custom-pagination";
import CustomBackButton from "@/components/custom/custom-back-button";
import { useLedgerEnv } from "@/hooks/use-ledger-env";
import { useLedgerAccount } from "@/hooks/use-ledger-accounts";
import { useLedgerEntries } from "@/hooks/use-ledger-entries";
import { getLedgerEntryColumns } from "./_components/ledger-entry-column";
import PostJournalDialog from "./_components/post-journal";

const RootWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
}));

const StatBox = styled(Box)(({ theme }) => ({
  flex: 1,
  borderRadius: 12,
  padding: "20px 24px",
  minHeight: 100,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  border: `1px solid ${theme.palette.divider}`,
}));

const LedgerAccountDetail = () => {
  const { accountNumber } = useParams();
  const { environment } = useLedgerEnv();
  const [journalOpen, setJournalOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);

  const { data: accountRes, isLoading } = useLedgerAccount(
    accountNumber,
    environment,
  );
  const account = accountRes?.data ?? null;

  const {
    data: entriesRes,
    isLoading: entriesLoading,
    isError: entriesError,
  } = useLedgerEntries(accountNumber, environment, {
    page: currentPage,
    size: pageSize,
  });

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={2}>
        <CircularProgress size={16} thickness={4} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Stack>
    );
  }

  if (!account) {
    return (
      <Typography color="text.secondary" p={4}>
        Account not found in {environment}.
      </Typography>
    );
  }

  const active = account.status === "ACTIVE";

  return (
    <RootWrapper>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={2}>
          <CustomBackButton />
          <Typography variant="h6" fontWeight={600}>
            {account.accountName}
          </Typography>
          <Chip
            label={active ? "Active" : "Inactive"}
            size="small"
            sx={{
              borderRadius: "999px",
              fontWeight: 500,
              fontSize: "0.75rem",
              backgroundColor: active ? "#ecfdf5" : "#fef2f2",
              color: active ? "#065f46" : "#991b1b",
            }}
          />
        </Stack>
        <CustomButton onClick={() => setJournalOpen(true)} sx={{ height: 35 }}>
          Post Journal
        </CustomButton>
      </Stack>

      <Card>
        <Stack direction="row" spacing={2} mb={2}>
          <StatBox>
            <Typography variant="caption" color="text.secondary">
              Current Balance
            </Typography>
            <Typography fontSize="1.4rem" fontWeight={700} mt={0.5}>
              {account.balance != null
                ? formatCurrency(account.balance, account.currency ?? "NGN")
                : "—"}
            </Typography>
          </StatBox>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          {[
            { label: "Account Number", value: account.accountNumber },
            { label: "Account Name", value: account.accountName },
            { label: "Account Type", value: account.accountType },
            // role is backend/ops-owned (Flyway) and may be null for most accounts.
            { label: "Role", value: account.role },
            { label: "Currency", value: account.currency },
            { label: "Status", value: account.status },
            { label: "Environment", value: account.environment },
            { label: "Created At", value: account.createdAt },
            { label: "Updated At", value: account.updatedAt },
          ].map(({ label, value }) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {label}
              </Typography>
              <Typography fontSize="0.875rem" fontWeight={500} mt={0.25}>
                {value ?? "N/A"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </Card>

      <Card>
        <Typography
          fontSize="0.75rem"
          fontWeight={600}
          textTransform="uppercase"
          letterSpacing="0.05em"
          color="text.secondary"
          mb={2}
        >
          Entries
        </Typography>

        <DataTable
          data={entriesRes?.data ?? []}
          columns={getLedgerEntryColumns()}
          isLoading={entriesLoading}
          isError={entriesError}
        />

        <Box mt={2}>
          <CustomPagination
            total={entriesRes?.totalCount}
            perPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      </Card>

      <PostJournalDialog
        open={journalOpen}
        onClose={() => setJournalOpen(false)}
        defaultDebit={account.accountNumber}
      />
    </RootWrapper>
  );
};

export default LedgerAccountDetail;
